import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from '../PlatformFunctionality';
import { styles } from './ProductCard.css';
import { ProductCardActions } from './ProductCardActions';
import PriceTag from '@jbhi-fi/lanyard-ui/components/PriceTag/PriceTag';
import { ProductCardHeader } from './ProductCardHeader';
import { getTitleCase } from '@jbhi-fi/utils/src/getTitleCase/getTitleCase';
import { Reviews } from '../Reviews';
import { getProductPricing } from './utils/getProductPricing';
import { CardOverlay, CardOverlayVariant } from './CardOverlay/CardOverlay';
import { NotifyMeModal } from '../NotifyMeModal/NotifyMeModal';
import { ProductCardProps, ProductVariant, ProductDetail } from './types';
import Checkbox from '@jbhi-fi/lanyard-ui/components/Checkbox';
import { MUSIC_DEPARTMENT_CODE, OVERLAY_VISIBLE_DURATION, SOFTWARE_DEPARTMENT_CODES } from './constants';
import { toCamelCase } from '@jbhi-fi/utils/src/toCamelCase/toCamelCase';
import { ActionButtonType } from '../PdpProductProvider/types';
import { getProductStatusLabel } from './utils/getProductStatusLabel';
import { ProductCardVariants } from './ProductCardVariants/ProductCardVariants';
import { CartActionState } from '../CartProvider/types';
import { submitTracking } from '../Tracking/utils/shopifyActionTrackingHelper';
import { trackProductClick } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackProductClick';
import { transformProductCardPropsForTracking } from './utils/transformProductCardPropsForTracking';
import { AlgoliaHitTransformer } from '../Algolia/transformers/AlgoliaHitTransformer';
import { addListInfoForSku } from './utils/listTracking';
import { createPortal } from 'react-dom';
import { MarketplaceSellerCallout } from '../Marketplace/MarketplaceSellerCallout/MarketplaceSellerCallout';
import { isFirefoxStrictModeEnabled } from '@jbhi-fi/utils/src/firefoxStrictMode';
import { ProgressiveImage } from '../hooks/useProgressiveImage/useProgressiveImage';
import { ProductImageFallback } from '../ProductImageFallback/ProductImageFallback';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';

export type ProductCardVariant = 'default' | 'tablet' | 'small' | 'fullWidth' | 'landscape' | 'minified';

export const ProductCard = ({
  actionButtonType,
  artist,
  averageProductReviewRating,
  bannerLabel,
  brandImageUrl,
  button,
  details,
  deliveryTag,
  departmentCode,
  handle,
  getIsCompared,
  isFamily,
  isLoggedIn,
  keyFeatures,
  numberOfProductReviews,
  path,
  queryString = '',
  pricing,
  perksPricing,
  productImage,
  productId,
  productGroupId,
  productTags,
  productVariants,
  promoTags,
  preamble,
  priceBannerLabel,
  getShouldShowCompare,
  shouldHideNotifyMe = false,
  shouldHideWishlistOverlay = false,
  shouldHideCartOverlay = false,
  shouldHideReviews = false,
  shouldHideSwatches = false,
  shouldHideWishlist = false,
  shouldHideBannerTag = false,
  shouldHidePromoTag = false,
  shouldRemainInAddedState = false,
  shouldDisplayWasPrice,
  shouldShowReviews,
  showPerksPrice,
  shouldHidePerksExclusiveDealsFromNonMembers,
  sku,
  title,
  trackingInformation,
  variant = 'default',
  isDynamicWidth = false,
  variantId,
  getIsInWishlist,
  onUpdateWishlist,
  onCompare,
  onAddToCart,
  onClick,
  onViewAlternatives,
  useKlaviyoSubscribe,
  useKlaviyoBackInStoreSubscribe,
  isPerksMember,
  isPerksEarlyAccess,
  isPerksExclusiveDeal,
  hiddenPreambleProductGroupIds,
  marketplaceSeller,
  toolTips,
  showOnHover = false,
  tooltipVariant = 'default',
  isProductCardDetailBadgesEnabled = false,
  isProductCardDetailsEnabled = false,
  tooltipPosition,
}: ProductCardProps) => {
  const [isNotifyMeModalOpen, setIsNotifyMeModalOpen] = useState(false);
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | undefined>();
  const [activeOverlay, setActiveOverlay] = useState<CardOverlayVariant>(null);
  const [cartState, setCartState] = useState<CartActionState>(CartActionState.Default);
  const navigate = useNavigate();
  const isMinifiedVariant = variant === 'minified';
  const activeSku = selectedVariant?.sku || sku;
  const activeVariantId = selectedVariant?.variantId || variantId;
  const variantImage = selectedVariant?.productImage || productImage;
  const variantHandle = selectedVariant?.handle || handle;
  const isSoftware = SOFTWARE_DEPARTMENT_CODES.includes(departmentCode ?? 0);
  const isMusic = departmentCode === MUSIC_DEPARTMENT_CODE;
  const hasActions = Boolean(onAddToCart || onCompare);
  const hasProductCompare = !isMinifiedVariant && onCompare && getShouldShowCompare?.(productGroupId);
  const hasViewAlternatives = onViewAlternatives !== undefined;
  const shouldShowFooter = hasProductCompare || hasViewAlternatives;
  const url = (path || `/products/${variantHandle}`) + queryString;
  const shouldDisplayReviews =
    !shouldHideReviews && shouldShowReviews && numberOfProductReviews && averageProductReviewRating;
  const shouldShowProductLogo = !isMinifiedVariant && !isSoftware && variant !== 'landscape' && Boolean(brandImageUrl);
  const shouldShowKeyFeatures = isProductCardDetailsEnabled && !!keyFeatures?.length;
  const shouldShowPreamble =
    isSoftware && preamble && !hiddenPreambleProductGroupIds?.includes(productGroupId?.toString() ?? '');
  const shouldShowArtist =
    isMusic && artist && !hiddenPreambleProductGroupIds?.includes(productGroupId?.toString() ?? '');
  const isFamilyCard = (isFamily && !shouldHideSwatches && variant !== 'landscape' && variant !== 'minified') ?? false;
  const pricingValues = getProductPricing({
    pricing,
    shouldDisplayWasPrice,
    perksPricing,
    isPerksMember,
    showPerksPrice,
    shouldHidePerksExclusiveDealsFromNonMembers,
    isPerksEarlyAccess,
    isPerksExclusiveDeal,
  });
  const isInStoreOnly = productTags?.includes('In Store Only');
  const buttonType: ActionButtonType = toCamelCase(selectedVariant?.button || button);
  const isActionButtonConfirmationEnabled =
    ['addToCart', 'buy', 'preOrder'].includes(buttonType) && shouldHideCartOverlay;
  const productStatusLabel = getProductStatusLabel(productTags);
  const shouldUseWishlist = !isMinifiedVariant && onUpdateWishlist && getIsInWishlist && !shouldHideWishlist;
  const shouldShowHeader = hasActions && shouldUseWishlist;
  const isCompared = getIsCompared?.(activeSku) ?? false;
  const currency = trackingInformation?.trackingDescriptor?.currency;
  const cardTracking = {
    gtm: trackingInformation?.gtm,
    ecommerceExtraProps: trackingInformation?.trackingDescriptor?.ecommerceExtraProps,
    productTags,
    pricing,
    reviewScore: averageProductReviewRating,
    reviewCount: numberOfProductReviews,
  };

  const shouldShowBannerLabel =
    !isMinifiedVariant && (!isPerksEarlyAccess || isPerksMember || (isPerksEarlyAccess && isPerksExclusiveDeal));

  const shouldShowPromoTags =
    !isMinifiedVariant && (!isPerksEarlyAccess || isPerksMember || (isPerksEarlyAccess && isPerksExclusiveDeal));

  const bottomContentPaddingSize = (() => {
    if (variant !== 'minified') return 'default';
    if (!hasViewAlternatives && !!deliveryTag && !!pricingValues?.cashback) return 'large';
    if (!hasViewAlternatives && !!deliveryTag) return 'small';
    return 'default';
  })();

  useEffect(() => {
    if (activeOverlay === 'checkout') {
      let shouldCancel = false;
      setTimeout(() => {
        if (!shouldCancel) setActiveOverlay(null);
      }, OVERLAY_VISIBLE_DURATION);
      return () => {
        shouldCancel = true;
      };
    }
  }, [activeOverlay]);

  useEffect(() => {
    if (!selectedVariant && productVariants?.length) {
      setSelectedVariant(productVariants?.find(variant => variant.variantId === variantId));
    }
  }, []);

  const handleNotifyMe = (e: any) => {
    e.preventDefault();
    setIsNotifyMeModalOpen(!isNotifyMeModalOpen);
  };

  const handleProductCompare = () => {
    if (hasProductCompare) {
      onCompare(activeSku, variantImage, trackingInformation, activeVariantId);
    }
  };

  const trackProductInteraction = (eventType: 'alternativeClick' | 'productClick') => {
    if (!trackingInformation) return;

    submitTracking({
      event: eventType,
      trackingDescriptor: trackingInformation.trackingDescriptor,
      gtm: trackingInformation?.gtm,
    });

    if (eventType !== 'productClick') return;

    trackProductClick({
      searchMethod: trackingInformation.trackingDescriptor.searchMethod,
      searchTerm: trackingInformation.trackingDescriptor.searchTerm,
      resultsFilters: trackingInformation.trackingDescriptor.resultsFilters,
      location: trackingInformation.trackingDescriptor.page,
      currency: currency ?? 'aud',
      itemListName: cardTracking?.ecommerceExtraProps?.list,
      itemListSlot: cardTracking?.ecommerceExtraProps?.itemListSlot,
      itemListServiceConfig: cardTracking?.ecommerceExtraProps?.itemListServiceConfig,
      clickedProduct: transformProductCardPropsForTracking(cardTracking),
    });
  };

  const handleViewAlternatives = () => {
    trackProductInteraction('alternativeClick');
    onViewAlternatives ? onViewAlternatives(activeSku, variantId, trackingInformation) : navigate(url);
  };

  const handleCardClick = () => {
    addListInfoForSku({
      sku,
      listName: cardTracking?.ecommerceExtraProps?.list,
      listSlot: cardTracking?.ecommerceExtraProps?.itemListSlot,
      listServiceConfig: cardTracking?.ecommerceExtraProps?.itemListServiceConfig,
    });
    trackProductInteraction('productClick');
    onClick?.(activeSku);
  };

  const handleUpdateWishlist = async (event: any) => {
    if (!shouldUseWishlist) return;

    try {
      if (isLoggedIn) {
        setIsWishlistLoading(true);
        await onUpdateWishlist({
          actionType: getIsInWishlist(activeSku) ? 'removeItem' : 'addItem',
          sku: activeSku,
          variantId: activeVariantId,
          eventData: trackingInformation,
          event,
        });
        setIsWishlistLoading(false);
        return;
      }

      if (trackingInformation) {
        submitTracking({
          event: 'wishlist',
          action: 'attempt',
          trackingDescriptor: trackingInformation.trackingDescriptor,
          gtm: trackingInformation?.gtm,
        });
      }

      if (!shouldHideWishlistOverlay) {
        setActiveOverlay('wishlist');
      }
    } catch (error) {
      console.error('Error updating wishlist', error);
      setIsWishlistLoading(false);
    }
  };

  const handleAddToCart = async (e: any) => {
    if (onAddToCart && cartState !== CartActionState.Updating) {
      setCartState(CartActionState.Updating);
      const isSuccess = await onAddToCart(e, activeSku, activeVariantId, trackingInformation);
      setCartState(CartActionState.Default);
      if (isSuccess) {
        if (!shouldHideCartOverlay && isSuccess) {
          setActiveOverlay('checkout');
        }
        return true;
      }
    }
    return false;
  };

  const handleAction = async (e: any) => {
    if (buttonType === 'notifyMe') {
      handleNotifyMe(e);
      return true;
    } else if (buttonType === 'view') {
      handleCardClick();
      navigate(url);
      return true;
    } else {
      return await handleAddToCart(e);
    }
  };

  return (
    <div className={`ProductCard ${styles.container({ variant, isDynamicWidth, hasActions })}`}>
      <CardOverlay
        activeOverlay={activeOverlay}
        onClose={() => setActiveOverlay(null)}
        onCreateAccount={event =>
          onUpdateWishlist?.({
            event,
            sku: activeSku,
            variantId: activeVariantId,
            actionType: 'addItem',
            notLoggedInAction: 'createAccount',
          })
        }
        onLogin={event =>
          onUpdateWishlist?.({
            event,
            sku: activeSku,
            variantId: activeVariantId,
            actionType: 'addItem',
            notLoggedInAction: 'login',
          })
        }
      />
      {shouldShowHeader ? (
        <ProductCardHeader
          bannerLabel={shouldShowBannerLabel ? bannerLabel : undefined}
          variant={variant}
          shouldHideWishlist={shouldHideWishlist}
          wishlistState={isWishlistLoading ? 'loading' : getIsInWishlist(activeSku) ? 'added' : 'default'}
          wishlistOnClick={handleUpdateWishlist}
          promoTags={shouldShowPromoTags ? promoTags : undefined}
          shouldHideBannerTag={shouldHideBannerTag}
          shouldHidePromoTag={shouldHidePromoTag}
          details={details as ProductDetail}
          productGroupId={productGroupId}
          isProductCardDetailBadgesEnabled={isProductCardDetailBadgesEnabled}
        />
      ) : null}
      <div
        className={`ProductCard_content ${styles.content({
          variant,
          hasActions,
          paddingSize: bottomContentPaddingSize,
        })}`}
        data-testid="product-card-content"
      >
        <Link
          href={url}
          className={`ProductCard_imageLink ${styles.link({ variant })}`}
          onClick={handleCardClick}
          data-testid="product-card-content-link"
        >
          <div className={`ProductCard_topContent ${styles.topContent({ variant })}`}>
            <div className={`ProductCard_imageBase ${styles.image({ variant })}`} data-testid="product-card-image-base">
              {variantImage ? (
                <ProgressiveImage
                  className={styles.image({ variant })}
                  {...AlgoliaHitTransformer.toProgressiveImageProps(
                    { product_image: variantImage, title },
                    { sizePx: 300 }
                  )}
                />
              ) : (
                <ProductImageFallback className={styles.image({ variant })} />
              )}
            </div>
            <div className={styles.linkContent({ variant })} data-testid="product-card-content-link-content">
              {shouldShowProductLogo ? (
                <div className={styles.productLogo({ variant })}>
                  <img className={styles.productLogoImage} src={brandImageUrl} alt={preamble} />
                </div>
              ) : null}
              <div className={styles.title({ variant })} data-testid="product-card-title">
                {title}
              </div>
              {shouldShowArtist ? (
                <div className={styles.artist}>{getTitleCase(artist)}</div>
              ) : shouldShowPreamble ? (
                <div className={styles.preamble}>{preamble}</div>
              ) : null}

              {shouldDisplayReviews ? (
                <div className={styles.reviews({ variant })} data-testid="product-card-reviews">
                  <Reviews
                    testId="product-card-reviews"
                    variant="productTile"
                    rating={averageProductReviewRating}
                    numberOfReviews={numberOfProductReviews}
                    onClick={handleCardClick}
                  />
                </div>
              ) : null}

              {marketplaceSeller ? (
                <MarketplaceSellerCallout
                  sellerName={marketplaceSeller.sellerName}
                  testId="marketplace-seller_callout_product-card"
                />
              ) : null}
              {shouldShowKeyFeatures ? (
                <ul className={styles.keyFeatures} data-testid="product-card-key-features">
                  {keyFeatures.map(keyFeature => (
                    <li key={`key-feature-${keyFeature.replace(' ', '').toLowerCase()}`} className={styles.keyFeature}>
                      {keyFeature}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </Link>

        <div className={`ProductCard_main ${styles.main({ variant })}`}>
          {isFamilyCard && productVariants && hasActions && variant !== 'landscape' ? (
            <ProductCardVariants
              variants={productVariants}
              selectedVariant={selectedVariant}
              setSelectedVariant={setSelectedVariant}
              variantType={variant}
            />
          ) : null}
          <div
            className={styles.bottomContent({
              variant,
              hasDeliverySpacing: !!deliveryTag,
              hasBannerTagSpacing: !!priceBannerLabel,
              hasCashbackSpacing: !!pricing?.displayPriceAfterCashback,
              hasTicketPriceSpacing: !!pricing?.wasPrice && shouldDisplayWasPrice,
              isDynamicWidth,
            })}
          >
            {pricingValues ? (
              <div
                className={`ProductCard_priceContainer ${styles.priceContainer({
                  variant,
                  isFamilyCard: !isMinifiedVariant,
                  hasMarketplaceCallout: !!marketplaceSeller,
                  addDeliveryMargin: !!deliveryTag && !hasActions,
                  hasCashBackTag: !!pricing?.displayPriceAfterCashback,
                })}`}
              >
                <Link href={url} className={styles.link({ variant: 'default' })} onClick={handleCardClick}>
                  <PriceTag
                    {...pricingValues}
                    variant={isMinifiedVariant ? 'extraSmall' : 'small'}
                    deliveryTag={deliveryTag}
                    bannerLabel={priceBannerLabel}
                    toolTips={toolTips}
                    showOnHover={showOnHover}
                    tooltipVariant={variant === 'minified' ? 'extraSmall' : tooltipVariant}
                    tooltipPosition={tooltipPosition}
                  />
                </Link>
              </div>
            ) : null}
            {hasActions ? (
              <>
                {buttonType === ActionButtonType.DoNotDisplay ||
                (buttonType === ActionButtonType.NotifyMe && isFirefoxStrictModeEnabled()) ? (
                  <div
                    data-testid="product-card-status"
                    className={styles.productStatusContainer({ isInStoreOnly, variant })}
                  >
                    {productStatusLabel}
                  </div>
                ) : (
                  <div
                    className={`ProductCard_actionsContainer ${styles.actionsContainer({
                      variant,
                      hasMarketplaceCallout: Boolean(marketplaceSeller),
                      hasDeliveryTag: Boolean(deliveryTag),
                      hasCashBackTag: !!pricing?.displayPriceAfterCashback,
                    })}`}
                  >
                    <ProductCardActions
                      variant={variant}
                      testId="product-card-actions"
                      type="action"
                      label={buttonType}
                      onAction={handleAction}
                      isLoading={cartState === CartActionState.Updating}
                      isConfirmationEnabled={isActionButtonConfirmationEnabled}
                      shouldRemainInAddedState={shouldRemainInAddedState}
                    />
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
        {shouldShowFooter ? (
          <div className={styles.footer({ variant, hasCompare: Boolean(hasProductCompare) })}>
            {hasProductCompare ? (
              <div className={styles.compareButtonContainer}>
                <Checkbox
                  checked={isCompared}
                  name="product-compare"
                  label="Compare"
                  onChange={() => handleProductCompare()}
                  id="product-compare"
                />
              </div>
            ) : null}
            {hasViewAlternatives && variant !== 'small' ? (
              <div
                className={styles.viewAlternativesButtonContainer({
                  hasCompare: Boolean(hasProductCompare),
                })}
              >
                <Button
                  onClick={handleViewAlternatives}
                  type="linkWithIcon"
                  label="View alternatives"
                  icon={<Icon name="ArrowRight" />}
                  dataTestId="product-card-view-alternatives-button"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {hasActions && !shouldHideNotifyMe
        ? createPortal(
            <NotifyMeModal
              isOpen={isNotifyMeModalOpen}
              onClose={() => setIsNotifyMeModalOpen(false)}
              useSubscribe={useKlaviyoSubscribe}
              useBackInStoreSubscribe={useKlaviyoBackInStoreSubscribe}
              variantId={activeVariantId}
              productTitle={title}
              productId={productId}
            />,
            document.body
          )
        : null}
    </div>
  );
};
